import type { ScormInteractionData } from './interactions'
import { cmiTimespanToMs, msToIso8601Duration } from './durations'

/** Enfants d'une interaction 1.2 (`cmi.interactions._children`). */
export const SCORM12_INTERACTION_CHILDREN = 'id,objectives,time,type,correct_responses,weighting,student_response,result,latency'

export const SCORM12_INTERACTION_KEY = /^(cmi\.interactions\.\d+\.)(.+)$/

/** Éléments d'interaction 1.2 renommés en 2004 (#158). */
const RENAMED: Record<string, string> = {
  student_response: 'learner_response',
  time: 'timestamp',
}

const CMI_TIME = /^\d{2}:\d{2}:\d{2}(?:\.\d{1,2})?$/

function localDay(day: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

/**
 * Traduit une écriture `cmi.interactions.n.*` 1.2 vers le modèle 2004 (#158) :
 * noms d'éléments, `wrong` → `incorrect`, latence CMITimespan → durée ISO 8601,
 * heure CMITime (sans date) → horodatage du jour. `null` = valeur invalide
 * (405), `undefined` = pas un élément d'interaction.
 */
export function scorm12InteractionWrite(key: string, value: string, now = new Date()): { key: string, value: string } | null | undefined {
  const match = SCORM12_INTERACTION_KEY.exec(key)
  if (!match) return undefined
  const [, prefix, field] = match
  if (field === 'latency') {
    const ms = cmiTimespanToMs(value)
    return ms === null ? null : { key, value: msToIso8601Duration(ms) }
  }
  if (field === 'time') {
    return CMI_TIME.test(value) ? { key: `${prefix}timestamp`, value: `${localDay(now)}T${value}` } : null
  }
  if (field === 'result' && value === 'wrong') return { key, value: 'incorrect' }
  return { key: `${prefix}${RENAMED[field] ?? field}`, value }
}

const TRUE_FALSE: Record<string, string> = { t: 'true', 1: 'true', f: 'false', 0: 'false' }

/** Une réponse 1.2 dans le format 2004 de son type ; inchangée si elle y est déjà. */
function responseTo2004(type: string | undefined, response: string): string {
  if (response.includes('[')) return response
  switch (type) {
    case 'true-false':
      return TRUE_FALSE[response] ?? response
    case 'choice':
    case 'sequencing':
      return response.replace(/^\{(.*)\}$/, '$1').split(',').join('[,]')
    case 'matching':
      return response.split(',').map(pair => pair.split('.').join('[.]')).join('[,]')
    default:
      return response
  }
}

/**
 * Réécrit les réponses d'une interaction 1.2 au format 2004 (#158). Fait à la
 * lecture et non à l'écriture : le type peut arriver après les réponses.
 */
export function scorm12ResponsesTo2004(interaction: ScormInteractionData): ScormInteractionData {
  const convert = (response: string) => responseTo2004(interaction.type, response)
  // Une réponse attendue numérique 1.2 est une valeur ; en 2004, un intervalle `min[:]max`.
  const convertCorrect = (pattern: string) =>
    interaction.type === 'numeric' && !pattern.includes('[') ? `${pattern}[:]${pattern}` : convert(pattern)
  return {
    ...interaction,
    ...(interaction.learnerResponse != null && { learnerResponse: convert(interaction.learnerResponse) }),
    ...(interaction.correctResponses && { correctResponses: interaction.correctResponses.map(convertCorrect) }),
  }
}
